<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

const props = defineProps({
	video: {
		type: Object,
		required: true,
	},
	stream: {
		type: Object,
		required: true,
	},
});

const rootEl = ref(null);
const playerContainer = ref(null);
const playerLoading = ref(true);
let playerInstance = null;
let bitmovinScriptPromise = null;

// Mirrors the scheduled-stream URL logic in DvrArchiveVideoPreviewModal so
// playback behaves identically, without touching that shared modal.
const getPlaybackUrl = () => {
	const { video, stream } = props;
	if (video.type === 'live-to-vod') {
		return `https://vod-archives.castr.com/${video.id}/index.m3u8`;
	}
	if (video.type === 'imported') {
		return `https://cstr-vod.castr.com/${video.importedVideoId}/index.m3u8`;
	}
	return `https://castr-preview.global.ssl.fastly.net/recorded-preview/${stream._id}/${video.id}/index.m3u8`;
};

const loadBitmovin = () => {
	if (window.bitmovin?.player) return Promise.resolve();
	if (bitmovinScriptPromise) return bitmovinScriptPromise;
	bitmovinScriptPromise = new Promise((resolve, reject) => {
		if (!document.getElementById('bitmovin-ui-css')) {
			const link = document.createElement('link');
			link.id = 'bitmovin-ui-css';
			link.rel = 'stylesheet';
			link.href = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer-ui.css';
			document.head.appendChild(link);
		}
		const script = document.createElement('script');
		script.src = 'https://cdn.bitmovin.com/player/web/8/bitmovinplayer.js';
		script.async = true;
		script.onload = () => resolve();
		script.onerror = reject;
		document.head.appendChild(script);
	});
	return bitmovinScriptPromise;
};

const destroyPlayer = async () => {
	if (playerInstance) {
		try {
			await playerInstance.destroy();
		} catch (_e) {
			// ignore
		}
		playerInstance = null;
	}
};

const loadPlayer = async () => {
	playerLoading.value = true;
	await destroyPlayer();
	try {
		await loadBitmovin();
	} catch (_e) {
		playerLoading.value = false;
		return;
	}
	await nextTick();
	if (!playerContainer.value || !window.bitmovin?.player) {
		playerLoading.value = false;
		return;
	}
	const config = {
		key: 'b495d0ae-5e81-4090-8529-fc00bf780cd9',
		playback: { autoplay: true, muted: true },
		style: { width: '100%' },
	};
	try {
		playerInstance = new window.bitmovin.player.Player(playerContainer.value, config);
		playerInstance.on('playing', () => { playerLoading.value = false; });
		playerInstance.on('error', () => { playerLoading.value = false; });
		await playerInstance.load({ hls: getPlaybackUrl() });
		playerLoading.value = false;
	} catch (_e) {
		playerLoading.value = false;
	}
};

onMounted(() => {
	nextTick(() => loadPlayer());
	// Bring the preview into view once the accordion has finished expanding,
	// so it isn't left half-off the bottom of the screen.
	setTimeout(() => {
		rootEl.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, 320);
});

onBeforeUnmount(() => {
	destroyPlayer();
});
</script>

<template>
	<div ref="rootEl" class="relative aspect-video w-full rounded-lg overflow-hidden bg-black">
		<div v-if="playerLoading"
			class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 text-surface-8 pointer-events-none">
			<span class="inline-block size-7 rounded-full border-2 border-surface-6 border-t-transparent animate-spin"></span>
			<span class="text-sm">Loading preview…</span>
		</div>
		<div ref="playerContainer" class="absolute inset-0 w-full h-full"></div>
	</div>
</template>
